// Client-side error reporting utility with data sanitization

type ErrorInfo = {
  message: string
  stack?: string
  componentStack?: string
  userAgent: string
  url: string
  timestamp: string
  errorBoundary?: boolean
  errorBoundaryProps?: Record<string, unknown>
}

// List of sensitive field names to sanitize
const SENSITIVE_FIELDS = [
  'password',
  'pass',
  'pwd',
  'secret',
  'token',
  'api_key',
  'apikey',
  'auth',
  'authorization',
  'cookie',
  'session',
  'credit_card',
  'cc',
  'cvv',
  'ssn',
  'pin',
  '_auth_code',
  'accessToken',
  'refreshToken',
  'jwt',
  'first',
  'second',
  'confirm',
  'turnstileToken',
  'twoFactorToken',
  'rememberMeToken',
  'registrationToken',
  'verificationToken',
  'resetToken'
]



// Sanitize a string to prevent code injection
function sanitizeString(str: string): string {
  // Replace dangerous patterns with safe placeholders instead of removing entire string
  let sanitized = str
  
  // Replace dangerous patterns with safe placeholders
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[SCRIPT_TAG_REMOVED]')
  sanitized = sanitized.replace(/javascript:/gi, '[JAVASCRIPT_PROTOCOL_REMOVED]')
  sanitized = sanitized.replace(/on\w+\s*=/gi, '[EVENT_HANDLER_REMOVED]')
  sanitized = sanitized.replace(/eval\s*\(/gi, '[EVAL_CALL_REMOVED](')
  sanitized = sanitized.replace(/Function\s*\(/gi, '[FUNCTION_CONSTRUCTOR_REMOVED](')
  sanitized = sanitized.replace(/setTimeout\s*\(/gi, '[SETTIMEOUT_REMOVED](')
  sanitized = sanitized.replace(/setInterval\s*\(/gi, '[SETINTERVAL_REMOVED](')
  sanitized = sanitized.replace(/require\s*\(/gi, '[REQUIRE_REMOVED](')
  sanitized = sanitized.replace(/import\s+.*from/gi, '[IMPORT_REMOVED]')
  sanitized = sanitized.replace(/process\.(env|exit|kill)/gi, '[PROCESS_METHOD_REMOVED]')
  sanitized = sanitized.replace(/fs\.(read|write|unlink)/gi, '[FS_METHOD_REMOVED]')
  sanitized = sanitized.replace(/__proto__|constructor|prototype/gi, '[PROTOTYPE_ACCESS_REMOVED]')
  
  // Additional sanitization: escape HTML entities and limit length
  return sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .substring(0, 1000) // Limit string length to prevent DoS
}


// Sanitize sensitive data from any object
export function sanitizeData(data: unknown): unknown {
  if (typeof data !== 'object' || data === null) {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item))
  }

  const sanitized: Record<string, unknown> = {}
  const dataObj = data as Record<string, unknown>
  
  for (const key in dataObj) {
    const lowerKey = key.toLowerCase()
    
    // Check if this field name contains sensitive keywords
    const isSensitive = SENSITIVE_FIELDS.some(field => 
      lowerKey.includes(field.toLowerCase())
    )
    
    if (isSensitive) {
      sanitized[key] = '[REDACTED]'
    } else if (typeof dataObj[key] === 'object') {
      sanitized[key] = sanitizeData(dataObj[key])
    } else if (typeof dataObj[key] === 'string') {
      // Check for patterns that look like sensitive data
      if (
        (dataObj[key] as string).length > 20 && // Long strings
        /^[a-zA-Z0-9+/=]+$/.test(dataObj[key] as string) // Base64-like
      ) {
        sanitized[key] = '[POSSIBLY_SENSITIVE]'
      } else {
        // Sanitize string for code injection protection
        sanitized[key] = sanitizeString(dataObj[key] as string)
      }
    } else if (typeof dataObj[key] === 'number' || typeof dataObj[key] === 'boolean') {
      // Numbers and booleans are safe
      sanitized[key] = dataObj[key]
    } else {
      // For other types, convert to string and sanitize
      sanitized[key] = sanitizeString(String(dataObj[key]))

    }
  }
  
  return sanitized
}

// Initialize error reporting
export function initErrorReporting() {
  if (typeof window === 'undefined') return

  // Add defensive checks for crypto wallet extensions
  try {
    // Prevent conflicts with ethereum property from crypto wallets
    if (typeof (window as unknown as { ethereum?: unknown }).ethereum !== 'undefined') {
      console.log('Crypto wallet detected, ensuring compatibility...');
    }
  } catch (_error) {
    // Ignore crypto wallet property conflicts
    console.warn('Crypto wallet property conflict ignored:', _error);
  }

  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    // Filter out crypto wallet extension errors
    if (event.message.includes('ethereum') || event.message.includes('crypto')) {
      console.warn('Crypto wallet error ignored:', event.message);
      return;
    }
    
    // Filter out generic "Script error." messages (cross-origin script failures)
    if (event.message === 'Script error.' || event.message === 'Script error') {
      console.warn('Generic script error ignored (likely cross-origin):', event.message);
      return;
    }
    
    // Filter out all browser extension errors
    const extensionPatterns = [
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'edge-extension://',
      'ms-browser-extension://',
      'browser-extension://',
      '/extensions/',
      'extension.js',
      'content-script',
      'content_script',
      'injected.js',
      'inpage.js'
    ];
    
    const isExtensionError = extensionPatterns.some(pattern => 
      event.message.includes(pattern) || 
      (event.error?.stack && event.error.stack.includes(pattern)) ||
      (event.filename && event.filename.includes(pattern))
    );
    
    if (isExtensionError) {
      console.warn('Browser extension error ignored:', event.message);
      return;
    }
    
    reportError({
      message: event.message,
      stack: event.error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    })
  })

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.toString() || String(event.reason);
    
    // Handle all Service Worker related errors
    const serviceWorkerErrorPatterns = [
      'Failed to update a ServiceWorker',
      'InvalidStateError.*ServiceWorker',
      'The object is in an invalid state.*ServiceWorker',
      'ServiceWorker.*invalid state',
      'ServiceWorker registration failed',
      'ServiceWorker script evaluation failed'
    ];
    
    const isServiceWorkerError = serviceWorkerErrorPatterns.some(pattern => {
      try {
        return new RegExp(pattern, 'i').test(message);
      } catch {
        return message.toLowerCase().includes(pattern.toLowerCase());
      }
    });
    
    if (isServiceWorkerError) {
      console.warn('Service Worker error (handled):', message);
      // Prevent the error from being reported to the server as it's handled
      event.preventDefault();
      return;
    }
    
    // Handle Server Action deployment sync errors
    if (message.includes('Failed to find Server Action') || 
        message.includes('This request might be from an older or newer deployment')) {
      console.warn('Server Action deployment sync error (handled):', message);
      // Prevent the error from being reported as it's a deployment sync issue
      event.preventDefault();
      return;
    }
    
    // Handle ethereum property conflicts from crypto wallet extensions
    if (message.includes('Cannot redefine property: ethereum')) {
      console.warn('Crypto wallet extension conflict (handled):', message);
      // Prevent the error from being reported as it's a third-party extension issue
      event.preventDefault();
      return;
    }
    
    // Handle all browser extension errors
    const extensionPatterns = [
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'edge-extension://',
      'ms-browser-extension://',
      'browser-extension://',
      '/extensions/',
      'extension.js',
      'content-script',
      'content_script',
      'injected.js',
      'inpage.js'
    ];
    
    const isExtensionError = extensionPatterns.some(pattern => 
      message.includes(pattern) || 
      (event.reason?.stack && event.reason.stack.includes(pattern))
    );
    
    if (isExtensionError) {
      console.warn('Browser extension error ignored:', message);
      // Prevent extension errors from being reported
      event.preventDefault();
      return;
    }
    
    reportError({
      message: `Unhandled Promise Rejection: ${message}`,
      stack: event.reason?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    })
  })
}

// Report error to server
export async function reportError(errorInfo: ErrorInfo) {
  try {
    // Sanitize the error info before sending
    const sanitizedInfo = sanitizeData(errorInfo)
    
    // Always send to server for tracking, but also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('CLIENT_ERROR:', sanitizedInfo)
    }

    await fetch('/api/client-error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'CLIENT_ERROR',
        ...(sanitizedInfo as Record<string, unknown>)
      }),
    })
  } catch (_err) {
    // Fail silently - we don't want error reporting to cause more errors
    console.error('Failed to report error:', _err)
  }
}

// React error boundary handler
export function handleErrorBoundary(error: Error, errorInfo: { componentStack: string }) {
  reportError({
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    errorBoundary: true,
  })
}
